// Framework-agnostic access to GET /rooms/:roomId/search (PRD-0004 §4). Kept
// separate from the react-query hooks so every surface (admin/seller/buyer)
// builds the same request and shares one query-key convention — the apps wrap
// this in a thin `useDocumentSearch` (their existing api/hooks.ts seam owns
// TanStack Query; shared owns the contract + fetch, exactly like `api`).
import { api } from './client';
import type {
  SearchDocumentsQuery,
  SearchDocumentsResponse,
  SearchResultDocument,
} from './types';

/** Normalize the (string | string[] | undefined) tagFilter into a stable array. */
export function normalizeTagFilter(tagFilter: SearchDocumentsQuery['tagFilter']): string[] {
  if (tagFilter == null) return [];
  return Array.isArray(tagFilter) ? tagFilter : [tagFilter];
}

/** True when the params carry at least one real criterion — a non-blank query,
 * a tag facet, or a doc-type. Callers gate fetching (and which dataset they
 * render) on this so an empty search bar falls back to the plain listing. */
export function hasSearchCriteria(params: SearchDocumentsQuery): boolean {
  return (
    Boolean(params.query && params.query.trim().length > 0) ||
    normalizeTagFilter(params.tagFilter).length > 0 ||
    Boolean(params.docType)
  );
}

/** Build the `/rooms/:roomId/search?…` path, encoding each tagFilter id as its
 * own repeated param (matches the endpoint's `z.array(uuid)` union). */
export function documentSearchPath(roomId: string, params: SearchDocumentsQuery): string {
  const sp = new URLSearchParams();
  const q = params.query?.trim();
  if (q) sp.set('query', q);
  for (const tagId of normalizeTagFilter(params.tagFilter)) sp.append('tagFilter', tagId);
  if (params.docType) sp.set('docType', params.docType);
  const qs = sp.toString();
  return `/rooms/${roomId}/search${qs ? `?${qs}` : ''}`;
}

/** Stable TanStack Query key for a search — tagFilter normalized to an array so
 * `'x'` and `['x']` cache identically. */
export function documentSearchQueryKey(
  roomId: string,
  params: SearchDocumentsQuery,
): [string, string, string, { query?: string; tagFilter: string[]; docType?: string }] {
  return [
    'rooms',
    roomId,
    'search',
    {
      query: params.query?.trim() || undefined,
      tagFilter: normalizeTagFilter(params.tagFilter),
      docType: params.docType,
    },
  ];
}

/** Fetch permission-scoped search hits for a room (unwraps `{ results }`). */
export function fetchDocumentSearch(
  roomId: string,
  params: SearchDocumentsQuery,
): Promise<SearchResultDocument[]> {
  return api
    .get<SearchDocumentsResponse>(documentSearchPath(roomId, params))
    .then((r) => r.results);
}
